/*
 * <product-loop-gallery>
 * Manual drag/swipe horizontal gallery of square images (no autoplay).
 * Two modes, chosen from the number of blocks vs. items-per-view:
 *   - loop  (blocks >= items-per-view): infinite drag. The list is cloned one
 *     group before and one group after the originals; while dragging we keep the
 *     offset within +/- half a group, shifting by a whole group when it crosses
 *     (visually seamless because the cloned content is identical).
 *   - centered-bounce (blocks < items-per-view): the list is centred, can be
 *     dragged a little (rubber-band), and snaps back to centre on release.
 * --ipv / --peek are read from CSS so the responsive breakpoints stay in the
 * stylesheet. Each instance is independent and theme-editor safe (re-init on
 * section reload, listeners cleaned up on disconnect).
 */
class ProductLoopGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-loop-gallery__track, [data-product-loop-track]');
    this.viewport = this.querySelector('.product-loop-gallery__viewport, [data-product-loop-viewport]');
    if (!this.track || !this.viewport) return;

    this.realItems = Array.from(this.track.children);
    if (!this.realItems.length) return;

    this.DRAG_THRESHOLD = 6; // px before a move counts as a drag (vs. a click)
    this.BOUNCE_DAMPING = 0.35; // rubber-band factor for centered mode
    this.BOUNCE_TRANSITION = 'transform 0.35s ease';

    this.isDown = false;
    this.dragged = false;
    this.startX = 0;
    this.startOffset = 0;
    this.offset = 0;
    this.translate = 0;
    this.activePointerId = null;
    this.hasPointerCapture = false;
    this.isInitialized = false;
    this.isReady = false;
    this.setupFrame = null;
    this.autoplayFrame = null;
    this.autoplayLastTime = null;
    this.autoplaySpeed = Math.max(0, parseFloat(this.dataset.autoplaySpeed) || 0);
    this.variableWidth = this.hasAttribute('data-variable-width');
    this.pauseOnHover = this.hasAttribute('data-pause-on-hover');
    this.isInViewport = !('IntersectionObserver' in window) || Boolean(window.Shopify?.designMode);

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onAutoplayFrame = this.onAutoplayFrame.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = (event) => event.preventDefault();
    this.onResize = this.debounce(this.setup.bind(this), 150);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    document.addEventListener('shopify:block:select', this.onBlockSelect);

    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
        rootMargin: '200px 0px',
      });
      this.intersectionObserver.observe(this);
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = entry.isIntersecting;
          if (this.isInViewport) this.startAutoplay();
          else this.stopAutoplay();
        },
        { threshold: 0.01 },
      );
      this.visibilityObserver.observe(this);
    } else {
      this.activate();
    }
  }

  activate() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('lostpointercapture', this.onPointerUp);
    this.track.addEventListener('click', this.onClickCapture, true);
    this.track.addEventListener('dragstart', this.onDragStart);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    this.setup();
  }

  onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.intersectionObserver?.disconnect();
    this.activate();
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('lostpointercapture', this.onPointerUp);
    this.track?.removeEventListener('click', this.onClickCapture, true);
    this.track?.removeEventListener('dragstart', this.onDragStart);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.onResize);
    if (this.setupFrame != null) cancelAnimationFrame(this.setupFrame);
    this.stopAutoplay();
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  // (Re)build layout from current viewport width and block count.
  setup() {
    if (!this.isInitialized) return;
    this.isReady = false;
    this.stopAutoplay();
    this.removeClones();
    this.track.style.transition = 'none';
    if (this.setupFrame != null) cancelAnimationFrame(this.setupFrame);
    this.setupFrame = requestAnimationFrame(() => this.measureAndRender());
  }

  measureAndRender() {
    this.setupFrame = null;
    const styles = getComputedStyle(this);
    this.ipv = parseFloat(styles.getPropertyValue('--ipv')) || 3;
    this.peek = parseFloat(styles.getPropertyValue('--peek')) || 0;

    this.width = this.viewport.clientWidth;
    this.itemWidth = this.width / (this.ipv + 2 * this.peek);
    this.count = this.realItems.length;
    this.groupWidth = this.variableWidth
      ? this.realItems.reduce((total, item) => total + item.getBoundingClientRect().width, 0)
      : this.count * this.itemWidth;
    this.loop = this.variableWidth ? this.groupWidth > this.width : this.count >= this.ipv;

    if (this.loop) {
      this.buildClones();
      // Place the first real item one "peek" in from the left edge.
      this.baseTranslate = -this.groupWidth + this.peek * this.itemWidth;
      this.offset = 0;
      this.setTranslate(this.baseTranslate);
    } else {
      this.centerTranslate = (this.width - this.groupWidth) / 2;
      this.setTranslate(this.centerTranslate);
    }

    this.isReady = true;
    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (
      !this.loop ||
      this.autoplaySpeed <= 0 ||
      !this.isInViewport ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    this.autoplayLastTime = performance.now();
    this.autoplayFrame = requestAnimationFrame(this.onAutoplayFrame);
  }

  stopAutoplay() {
    if (this.autoplayFrame != null) cancelAnimationFrame(this.autoplayFrame);
    this.autoplayFrame = null;
    this.autoplayLastTime = null;
  }

  onAutoplayFrame(timestamp) {
    if (!this.isReady || !this.loop || !this.isInViewport) {
      this.stopAutoplay();
      return;
    }

    const elapsed = Math.min(timestamp - this.autoplayLastTime, 64);
    this.autoplayLastTime = timestamp;

    if (!this.isDown && !document.hidden && !(this.pauseOnHover && this.matches(':hover'))) {
      this.offset += this.autoplaySpeed * (elapsed / 1000);
      this.normalizeOffset();
      this.setTranslate(this.baseTranslate + this.offset);
    }

    this.autoplayFrame = requestAnimationFrame(this.onAutoplayFrame);
  }

  removeClones() {
    this.track.querySelectorAll('[data-plg-clone] video').forEach((video) => video.pause());
    this.track.querySelectorAll('[data-plg-clone]').forEach((node) => node.remove());
  }

  // Clone one whole group before and after the originals for seamless wrapping.
  buildClones() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.realItems.forEach((item) => {
      const head = item.cloneNode(true);
      const tail = item.cloneNode(true);
      [head, tail].forEach((clone) => {
        clone.setAttribute('data-plg-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('video').forEach((video) => {
          video.classList.remove('is-playing');
          video.removeAttribute('autoplay');
          video.autoplay = false;
          video.preload = 'none';
          video.pause();
        });
      });
      before.appendChild(head);
      after.appendChild(tail);
    });

    this.track.insertBefore(before, this.realItems[0]);
    this.track.appendChild(after);
  }

  setTranslate(x) {
    this.translate = x;
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  // Keep the loop offset within +/- half a group so the visible window always
  // sits inside the cloned content; a whole-group shift is visually seamless.
  normalizeOffset() {
    const half = this.groupWidth / 2;
    while (this.offset > half) this.offset -= this.groupWidth;
    while (this.offset < -half) this.offset += this.groupWidth;
  }

  onPointerDown(event) {
    if (!this.isReady || (event.pointerType === 'mouse' && event.button !== 0)) return;
    this.isDown = true;
    this.dragged = false;
    this.startX = event.clientX;
    this.startOffset = this.offset;
    this.activePointerId = event.pointerId;
    this.hasPointerCapture = false;
  }

  startDragging(event) {
    this.dragged = true;
    this.track.style.transition = 'none';
    this.track.classList.add('is-grabbing');
    try {
      this.track.setPointerCapture(event.pointerId);
      this.hasPointerCapture = true;
    } catch (error) {
      /* setPointerCapture can throw if the pointer is already gone */
    }
  }

  onPointerMove(event) {
    if (!this.isDown || event.pointerId !== this.activePointerId) return;
    const dx = event.clientX - this.startX;
    if (!this.dragged) {
      if (Math.abs(dx) <= this.DRAG_THRESHOLD) return;
      this.startDragging(event);
    }

    if (this.loop) {
      this.offset = this.startOffset + dx;
      this.normalizeOffset();
      this.setTranslate(this.baseTranslate + this.offset);
    } else {
      this.setTranslate(this.centerTranslate + dx * this.BOUNCE_DAMPING);
    }
  }

  onPointerUp(event) {
    if (!this.isDown || event.pointerId !== this.activePointerId) return;
    const wasDragged = this.dragged;
    this.isDown = false;
    this.activePointerId = null;
    this.track.classList.remove('is-grabbing');
    try {
      if (this.hasPointerCapture && event.pointerId != null) {
        this.track.releasePointerCapture(event.pointerId);
      }
    } catch (error) {
      /* releasePointerCapture can throw if capture was already lost */
    } finally {
      this.hasPointerCapture = false;
    }

    if (wasDragged && !this.loop) {
      this.track.style.transition = this.BOUNCE_TRANSITION;
      this.setTranslate(this.centerTranslate);
    }
  }

  // Suppress the click that follows a drag so links don't fire on swipe.
  onClickCapture(event) {
    if (this.dragged) {
      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }
  }

  onBlockSelect(event) {
    if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
    this.intersectionObserver?.disconnect();
    const wasInitialized = this.isInitialized;
    this.activate();
    if (wasInitialized) this.setup();
  }
}

if (!customElements.get('product-loop-gallery')) {
  customElements.define('product-loop-gallery', ProductLoopGallery);
}
